<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">從劇本建立活動</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="$router.back()">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          {{ submitting ? '建立中...' : '建立 Campaign 草稿' }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="!loadingPlaybooks" class="info-banner">
      <i class="ri-information-line"></i>
      <div class="info-content">
        <strong>劇本模板</strong>會依您選擇的場景，自動建立觸發條件、等待時間與動作步驟。建立後請在編輯器補齊推播範本、優惠券等必要資源，通過啟用前檢查後即可上線。
      </div>
    </div>

    <div v-if="loadingPlaybooks" class="loading-state">
      <div class="spinner"></div>
      <p>載入劇本...</p>
    </div>

    <form v-else class="form-container" @submit.prevent="handleSubmit">
      <div class="form-section">
        <div class="form-group">
          <label class="form-label">選擇劇本 <span class="required">*</span></label>
          <select v-model="form.playbookCode" class="form-control" required>
            <option value="">請選擇</option>
            <option v-for="pb in playbooks" :key="pb.playbookCode" :value="pb.playbookCode">
              {{ pb.playbookName }}（{{ pb.playbookCode }}）
            </option>
          </select>
          <small v-if="selectedPlaybook" class="form-text">{{ selectedPlaybook.description }}</small>
        </div>

        <div v-if="selectedPlaybook?.stepPreview?.length" class="step-preview">
          <p class="hint-title">此劇本流程預覽：</p>
          <ol>
            <li v-for="step in selectedPlaybook.stepPreview" :key="step.stepOrder">
              {{ step.summary }}
            </li>
          </ol>
        </div>

        <div class="form-group">
          <label class="form-label">活動名稱 <span class="required">*</span></label>
          <input v-model="form.campaignName" type="text" class="form-control" required />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">開始時間</label>
            <input v-model="form.startAt" type="datetime-local" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label">結束時間</label>
            <input v-model="form.endAt" type="datetime-local" class="form-control" />
          </div>
        </div>

        <div v-if="selectedPlaybook?.requiredBindings?.length" class="binding-hint">
          <p class="hint-title">此劇本待填資源（建立後可於編輯器補齊）：</p>
          <ul>
            <li v-for="b in selectedPlaybook.requiredBindings" :key="b.bindingKey">
              {{ b.label }}（{{ b.bindingKey }}）
            </li>
          </ul>
        </div>
      </div>
    </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { showError, showSuccess } from '@/utils/swal.js';

function toIsoLocal(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

export default {
  name: 'CreateMarketingCampaignFromPlaybook',
  setup() {
    const router = useRouter();
    const playbooks = ref([]);
    const loadingPlaybooks = ref(true);
    const submitting = ref(false);
    const form = ref({
      playbookCode: '',
      campaignName: '',
      startAt: '',
      endAt: ''
    });

    const selectedPlaybook = computed(() =>
      playbooks.value.find((p) => p.playbookCode === form.value.playbookCode)
    );

    const loadPlaybooks = async () => {
      loadingPlaybooks.value = true;
      try {
        const res = await marketingCampaignService.getPlaybooks();
        const data = res.data?.data ?? res.data;
        playbooks.value = data?.items || [];
      } catch (e) {
        showError(e.response?.data?.message || e.message || '載入劇本失敗');
      } finally {
        loadingPlaybooks.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!form.value.playbookCode) {
        showError('請選擇劇本');
        return;
      }
      if (!form.value.campaignName.trim()) {
        showError('請填寫活動名稱');
        return;
      }
      if (form.value.startAt && form.value.endAt && new Date(form.value.startAt) > new Date(form.value.endAt)) {
        showError('開始時間不可晚於結束時間');
        return;
      }

      submitting.value = true;
      try {
        const payload = {
          playbookCode: form.value.playbookCode,
          campaign: {
            campaignName: form.value.campaignName.trim(),
            startAt: toIsoLocal(form.value.startAt),
            endAt: toIsoLocal(form.value.endAt)
          },
          resourceBindings: {},
          creationSource: 'manual'
        };
        const res = await marketingCampaignService.instantiateFromPlaybook(payload);
        const data = res.data?.data ?? res.data;
        showSuccess('Campaign 草稿建立成功');
        router.push({ name: 'EditMarketingCampaign', params: { id: data.campaign.id } });
      } catch (e) {
        showError(e.response?.data?.message || e.message || '建立失敗');
      } finally {
        submitting.value = false;
      }
    };

    onMounted(loadPlaybooks);

    return {
      playbooks,
      loadingPlaybooks,
      form,
      selectedPlaybook,
      submitting,
      handleSubmit
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
</style>

<style scoped>
.form-container {
  max-width: 720px;
}

.form-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #3b82f6;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #1e40af;
  line-height: 1.6;
  max-width: 720px;
}

.info-banner i {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-content strong {
  font-weight: 600;
}

.binding-hint {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.step-preview {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.step-preview ol {
  margin: 0;
  padding-left: 20px;
  color: #555;
  line-height: 1.7;
}

.hint-title {
  margin: 0 0 8px;
  font-weight: 600;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>
